import React, { useEffect, useState } from "react";
import { useStripe } from "@stripe/react-stripe-js";
import { IconButton, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import cn from "classnames";
import { useTranslate } from "../../../../../hooks/useTranslate";
import Preloader from "../../../../../components/Preloader";
import { useStudents } from "../../../../../context/students";
import SummaryBox from "../../../../../components/SummaryBox";
import Agreements from "../../../EnrolmentAgreement/components/Agreements";
import axiosManager from "../../../../../services/axiosManager";
import { useGetContract } from "../../../../../hooks/useGetContract";
import { useToast } from "../../../../../context/toast";
import AgreementBox from "../../../EnrolmentAgreement/components/AgreementBox";
import useStatuses from "../../../../../context/status/useStatuses";
import Select from "../../../../../components/Select";

const ContractBlock = (props) => {
    const {
        isOpen, onOpenClick, onClose,
        studentFullName,
        disabled,
        agreement, changeAgreement,
    } = props;
    const { t } = useTranslate();
    const { activeStudentId } = useStudents();
    const { generalStatuses } = useStatuses();
    const toast = useToast();

    const { isLoading, isRefetching, contract } = useGetContract(activeStudentId);

    const {
        isFetching: isLoadingOptions, data: agreementOptions,
    } = useQuery({
        queryKey: [`student/${activeStudentId}/agreement-options`],
        queryFn: () => axiosManager.get$(`/parent/api/students/${activeStudentId}/agreement-options`).then((response) => response?.data?.availableOptions),
        retry: 0,
        cacheTime: 0,
        keepPreviousData: true,
        enabled: !!activeStudentId,
    });

    const showRolling = !generalStatuses.student?.studyingIb && (agreementOptions || []).includes("Rolling");

    useEffect(() => {
        if (activeStudentId) {
            changeAgreement(contract?.contractType ?? undefined);
            if (contract?.contractType) onClose();
        }
    }, [activeStudentId, contract?.contractType]);

    const {
        mutateAsync: updateContractType, isPending: isUpdatingContractType,
    } = useMutation({
        mutationFn: (contractType) => axiosManager.post$(`/parent/api/students/${activeStudentId}/contracts/${contract?.contractId}/SetContractType?contractType=${contractType}`).then((response) => response?.data),
        onError: () => toast.error(t("common.errorMessage")),
    });

    const onSelectAgreement = async (contractType) => {
        try {
            await updateContractType(contractType);
            changeAgreement(contractType);
            onClose();
        } catch {
            // error in mutate
        }
    };

    return (
        <SummaryBox isFormBox showBorder={isOpen}>
            <Preloader
                isLoading={isLoading || isRefetching || isLoadingOptions || isUpdatingContractType}
                overflow
                showInitiallyOverflow
            >
                <div className="flex xs:flex-column align-center justify-between gap-4">
                    <div>
                        <Typography variant="h4" className="text-primary">
                            {t("tuitionBilling.agreementLengthTitle")}
                        </Typography>
                        <Typography variant="body1" className="mt-2">
                            {t("tuitionBilling.agreementLengthSubTitle", { studentName: studentFullName })}
                        </Typography>
                    </div>
                    {!isOpen && (
                        <div className="flex items-center gap-4">
                            <div className={cn("billing-block__selected", { "billing-block__selected-disabled": disabled })}>
                                <Typography variant="body1">{t("tuitionBilling.selectedAgreement")}</Typography>
                                <Typography variant="h4" className="text-primary">
                                    {(agreement === "Annual" ? t("enrolmentAgreement.annualAgreement.title") : t("enrolmentAgreement.perSemesterAgreement.title")) || "-"}
                                </Typography>
                            </div>
                            <IconButton
                                aria-label="edit"
                                className="billing-block__edit-btn"
                                disableRipple
                                onClick={onOpenClick}
                                disabled={disabled}
                            >
                                <EditOutlinedIcon />
                            </IconButton>
                        </div>
                    )}
                </div>
                {isOpen && (
                    <div className="mt-12 flex flex-wrap align-stretch gap-8">
                        <AgreementBox
                            isRecommended
                            disabled={isUpdatingContractType}
                            title={t("enrolmentAgreement.annualAgreement.title")}
                            isSelected={agreement === "Annual"}
                            onClick={() => onSelectAgreement("Annual")}
                        >
                            {t("enrolmentAgreement.annualAgreement.point1")}
                        </AgreementBox>
                        {showRolling && (
                            <AgreementBox
                                disabled={isUpdatingContractType}
                                title={t("enrolmentAgreement.perSemesterAgreement.title")}
                                isSelected={agreement === "Rolling"}
                                onClick={() => onSelectAgreement("Rolling")}
                            >
                                {t("enrolmentAgreement.perSemesterAgreement.point1")}
                            </AgreementBox>
                        )}
                    </div>
                )}
            </Preloader>
        </SummaryBox>
    );
};

export default ContractBlock;
